import { useState } from 'react';

import { Stack } from 'ui/stack';
import { Box } from 'ui/box';

import { Loader } from 'ui/loader';
import { Select } from 'ui/select';

import GoalItem from 'containers/user/goals/goal-item';
import GoalContactItem from 'containers/user/goals/goal-contact-item';
import { getSortedGoalCategoryGroups } from 'containers/user/goals/goals.helpers';
import useGoals from 'containers/user/goals/use-goals';

import type { YearOption } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-filters/goals-matrix-filters';

import { getCurrentYear } from 'helpers/date.helpers';

const GoalsPanel = (): React.JSX.Element => {
  const currentYear = getCurrentYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  const years: YearOption[] = [currentYear, currentYear - 1].map((year) => ({
    value: year,
    label: String(year),
  }));

  const { loading, goals } = useGoals(selectedYear);
  const sortedCategoryGroups = getSortedGoalCategoryGroups(goals);

  return (
    <Box sx={{ position: 'relative' }}>
      <Loader loading={loading} fullScreen={false} />

      <Stack
        direction="row"
        sx={{
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          pt: 2,
          pb: 3,
        }}
      >
        <p className="max-w-[672px] text-base text-gray-700">
          Welcome to the Client Goals page. MARKETview understands that your
          institution’s goals are complex and unique. We provide personalized
          KPIs by identifying the Critical Elements that directly support your
          strategic objectives, giving you clear, actionable insights to drive
          success.
        </p>
        <Select<YearOption>
          options={years}
          value={years.find((year) => year.value === selectedYear)}
          onChange={(option) => {
            if (option && !Array.isArray(option)) {
              setSelectedYear(option.value);
            }
          }}
          sx={{ width: 96 }}
          aria-label="Select year"
          data-testid="monitoring-goals-fiscal-year-select"
        />
      </Stack>

      {sortedCategoryGroups.map(({ category, goals: categoryGoals }) => (
        <Stack key={category} sx={{ mb: 3 }}>
          <h2
            className="text-lg font-bold text-gray-700 mb-2"
            data-testid="goal-category-heading"
          >
            {category} goals
          </h2>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
            {categoryGoals.map((goal) => (
              <GoalItem
                key={goal.id}
                goal={goal}
                isMonitoringYear={false}
                selectedYear={selectedYear}
                hideMonitoringDetails
              />
            ))}
          </Stack>
        </Stack>
      ))}

      <Box sx={{ pt: 3 }}>
        <GoalContactItem variant="monitoring" />
      </Box>
    </Box>
  );
};

export default GoalsPanel;
